import { useEffect, useState } from 'react'

// Share of the bar's height taken by the refracting band along the edge (the
// Figma glass "splay"). Inside it the displacement grows quadratically toward the
// edge; at the centre there is none, like looking through the flat middle of a lens.
export const LENS_SPLAY = 0.3

// How long the element's size has to hold still before the map is rebuilt for it.
const LENS_SETTLE_MS = 160

// Only Chromium renders an SVG `url(#…)` filter inside `backdrop-filter`; WebKit
// and Gecko parse it and then draw nothing, so `CSS.supports` alone is not enough.
function isLensCapable() {
  if (typeof window === 'undefined' || typeof CSS === 'undefined') return false
  if (!CSS.supports('backdrop-filter', 'url(#lens)') && !CSS.supports('-webkit-backdrop-filter', 'url(#lens)')) {
    return false
  }
  const brands = navigator.userAgentData?.brands
  if (Array.isArray(brands)) return brands.some(({ brand }) => /Chromium/i.test(brand))
  const ua = navigator.userAgent || ''
  return /Chrome\//.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)
}

// Builds a displacement map for a rounded rectangle of `width` × `height` with
// `cornerRadius` (a pill when it is half the shorter side). Red drives the
// horizontal offset and green the vertical, both centred on 128. Each rim pixel
// points outward so the edge shows the backdrop beyond it squeezed into the rim,
// which is how a thick rounded slab of glass bends what sits behind it.
function buildDisplacementMap(width, height, rimPx, cornerRadius) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(width * dpr)
  canvas.height = Math.ceil(height * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const image = ctx.createImageData(canvas.width, canvas.height)
  const data = image.data
  const halfMin = Math.min(width, height) / 2
  const radius = Math.min(cornerRadius ?? halfMin, halfMin)
  const halfW = width / 2
  const halfH = height / 2
  // The band can never reach the centre: at least the inner fifth of the shorter
  // side stays flat, so a small surface still reads as glass and not as a smear.
  // The cap sits just above the tab bar pill's 16px depth, which stays as tuned.
  const rim = Math.min(rimPx || height * LENS_SPLAY, halfMin * 0.8)

  // Signed distance to the pill outline; negative inside.
  const sdf = (x, y) => {
    const qx = Math.abs(x - halfW) - (halfW - radius)
    const qy = Math.abs(y - halfH) - (halfH - radius)
    const ox = Math.max(qx, 0)
    const oy = Math.max(qy, 0)
    return Math.hypot(ox, oy) + Math.min(Math.max(qx, qy), 0) - radius
  }

  const eps = 0.5
  for (let j = 0; j < canvas.height; j++) {
    for (let i = 0; i < canvas.width; i++) {
      const x = (i + 0.5) / dpr
      const y = (j + 0.5) / dpr
      const inside = -sdf(x, y)
      let m = inside >= rim ? 0 : 1 - Math.max(inside, 0) / rim
      m *= m
      const nx = (sdf(x + eps, y) - sdf(x - eps, y)) / (2 * eps)
      const ny = (sdf(x, y + eps) - sdf(x, y - eps)) / (2 * eps)
      const k = (j * canvas.width + i) * 4
      data[k] = Math.round(128 + 127 * nx * m)
      data[k + 1] = Math.round(128 + 127 * ny * m)
      data[k + 2] = 128
      data[k + 3] = 255
    }
  }
  ctx.putImageData(image, 0, 0)
  return canvas.toDataURL()
}

// Keeps a displacement map the size of the element behind `ref`. Returns null
// where the lens cannot render, so callers fall back to the plain tint. Options:
// `enabled` re-arms the hook for elements that mount after the first render,
// `rim` fixes the refracting band in px (the Figma glass "depth") instead of
// deriving it from the height, and is clamped so the centre stays flat. `cornerRadius`
// is the element's border radius in px; left out, the outline is a pill.
export default function useGlassLens(ref, options = {}) {
  const { enabled = true, rim, cornerRadius } = options
  const [lens, setLens] = useState(null)

  useEffect(() => {
    const node = ref.current
    if (!enabled || !node || !isLensCapable()) return undefined

    // Building the map is a per-pixel pass plus a PNG encode, far too much for
    // every frame of a scroll or a pill flight. The filter region follows the
    // element's size at once (feImage stretches the last map to fit) and the map
    // itself is built only once the size has held still for LENS_SETTLE_MS, and
    // only once per size: the bar only ever settles at its full and compact sizes.
    const maps = new Map()
    let size = { width: 0, height: 0 }
    let settle = 0
    const mapFor = ({ width, height }) => {
      const key = `${width}x${height}`
      if (!maps.has(key)) maps.set(key, buildDisplacementMap(width, height, rim, cornerRadius))
      return maps.get(key)
    }
    const rebuild = () => {
      const href = mapFor(size)
      setLens(current => (current?.href === href ? current : href ? { ...size, href } : null))
    }
    const resize = next => {
      if (!next.width || !next.height || (next.width === size.width && next.height === size.height)) return
      size = next
      clearTimeout(settle)
      setLens(current => {
        if (!current) return (rebuild(), current)
        return { ...current, ...size }
      })
      settle = setTimeout(rebuild, LENS_SETTLE_MS)
    }
    const measure = () => resize({ width: Math.round(node.offsetWidth), height: Math.round(node.offsetHeight) })

    measure()
    let unsubscribe
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      unsubscribe = () => window.removeEventListener('resize', measure)
    } else {
      const observer = new ResizeObserver(([entry]) => {
        const box = entry.borderBoxSize?.[0]
        if (box) resize({ width: Math.round(box.inlineSize), height: Math.round(box.blockSize) })
        else measure()
      })
      observer.observe(node)
      unsubscribe = () => observer.disconnect()
    }
    return () => {
      clearTimeout(settle)
      unsubscribe()
    }
  }, [ref, enabled, rim, cornerRadius])

  return lens
}
